import {SubAPI} from "./SubAPI";
import {OpeningType, Preps} from "./Products";
import {InventoryKind} from "./Logistics";
import {OpeningLogiCtrlStatus} from "./Openings";
import {Cents} from "nate-react-api-helpers";

export class LogiCtrl extends SubAPI {

    listStatuses(input: {
        project: number;
        opening?: number;
        div10?: number;
        miscProduct?: number;
        glazing?: number;
    }) {
        return this.fetcher.get<LogiCtrlStatus[]>("/api/logictrl/statuses", input)
    }

    listRules(input: {
        project: number;
        opening?: number;
        div10?: number;
        miscProduct?: number;
        glazing?: number;
    }) {
        return this.fetcher.get<LogiCtrlRule[]>("/api/logictrl/rules", input)
    }

    listHwRuleSummary(input: {
        openings: number[]
    }) {
        return this.fetcher.post<LogiCtrlRule[]>("/api/logictrl/get-hw-rule-summary", input)
    }

    listHwStatusSummary(input: {
        openings: number[]
        project: number;
    }) {
        return this.fetcher.post<LogiCtrlStatus[]>("/api/logictrl/get-hw-status-summary", input)
    }

    upsertRules(input: {
        rules: LogiCtrlRule[],
        project: number;
        opening: number | null;
        div10: number | null;
        miscProduct: number | null;
        glazing: number | null;
    }) {
        return this.fetcher.post<{task: string}>("/api/logictrl/rules", input)
    }

    upsertHwRules(input: {
        rules: LogiCtrlRule[],
        project: number;
        openings: number[];
    }) {
        return this.fetcher.post<{task: string}>("/api/logictrl/hw-rules", input)
    }

    bulkUpsertRules(input: {
        project: number;
        openings: number[];
        doorAction: LogiCtrlRuleAction;
        frameAction: LogiCtrlRuleAction;
        hwAction: LogiCtrlRuleAction;
    }) {
        return this.fetcher.post<{task: string}>("/api/logictrl/bulk-opening-rules", input)
    }

    listExisting(input: {
        project: number;
        opening?: number;
        div10?: number;
        miscProduct?: number;
        glazing?: number;
    }) {
        return this.fetcher.get<LogiCtrlExistingItem[]>("/api/logictrl/existing", input)
    }

    upsertExisting(input: LogiCtrlExistingItem) {
        return this.fetcher.post<{}>("/api/logictrl/existing", input)
    }

    applyExisting(input: {
        project: number;
        opening?: number;
        div10?: number;
        miscProduct?: number;
        glazing?: number;
        skipInvAutoRuleSetup?: boolean;
    }) {
        return this.fetcher.post<{task: string}>("/api/logictrl/existing/apply", input)
    }

    applyBulkChange(input: {
        action: "skip" | "return";
        project: number;
        ids: number[];
        skipInvAutoRuleSetup?: boolean;
    }) {
        return this.fetcher.post<{task: string}>("/api/logictrl/existing/apply-bulk", input)
    }

    setPoModifyApproval(input: {
        project: number;
        ids: number[];
        approved: boolean;
    }) {
        return this.fetcher.post<{task: string}>("/api/logictrl/existing/po-modify", input)
    }

    listChanges(input: {
        project: number;
    }) {
        return this.fetcher.get<LogiCtrlChangeItem[]>("/api/logictrl/changes/groupped", input)
    }

    listPendingChanges(input: {
        project: number;
        changeset?: number;
    }) {
        return this.fetcher.get<LogiCtrlPendingChange[]>("/api/logictrl/changes", input)
    }

    upsertPrice(input: LogiCtrlPendingChange) {
        return this.fetcher.post<LogiCtrlPendingChange>("/api/logictrl/change", input)
    }

    listWorkOrders(input: {project: number; changeset?: number}) {
        return this.fetcher.get<LogiCtrlWorkOrderPrice[]>("/api/logictrl/work-orders", input)
    }

    upsertWorkOrder(input: LogiCtrlWorkOrderPrice) {
        return this.fetcher.post<LogiCtrlWorkOrderPrice>("/api/logictrl/work-order", input)
    }
}

export type LogiCtrlStatus = {
    id: number;
    opening: number | null;
    hardwareGroup: number | null;
    hardwareGroupItem: number | null;
    miscProduct: number | null;
    div10: number | null;
    glazing: number | null;
    kind: InventoryKind;
    addProduct: number | null;
    rmInvRequest: number | null;
    detailJson: {
        items: any[] | null | undefined;
        remove: {} | null | undefined
    };
    isChange: boolean;
    attempted: boolean;
    error: string | null;
    project: number;
    subtype: OpeningType | null;
    displayName: string | null;
    autoReplace: boolean;
    removeInvRequests: number[] | null | undefined;
}

export type LogiCtrlRule = {
    id: number;
    project: number;
    kind: InventoryKind | null;
    opening: number | null;
    subtype: OpeningType | null;
    hardwareGroupItem: number | null;
    div10Item: number | null;
    glazingItem: number | null;
    miscProduct: number | null;
    inventoryRequest: number | null;
    action: LogiCtrlRuleAction;
}

export type LogiCtrlRuleAction = "skip" | "send" | "not-released-replace" | null;

export type LogiCtrlExistingItem = {
    id: number;
    kind: InventoryKind;
    subtype: OpeningType | null;
    name: string;
    qty: number;
    autoReplace: boolean;
    leftBySkip: boolean; // the address was skipped, so the default recalculate left this request
    errorStr?: string | null;
    status: string;
    action: LogiCtrlExistingAction;
    actionIsDefault: boolean; // the stored action is the status default, not a user's pick

    div10Item: number | null;
    glazingItem: number | null;
    opening: number | null;
    miscProduct: number | null;

    openingName: string | null;

    // the draft / price-request PO the request is on, if any
    purchaseOrder: number | null;
    purchaseOrderNumber: string | null;
    purchaseOrderStatus: string | null;
    purchaseOrderSupplierName: string | null;
    purchaseOrderTargetDate: string | null;
    purchaseOrderLineCount: number;
    purchaseOrderItemDescription: string | null;
    purchaseOrderItemQty: number | null;

    // released or on a draft PO: "Recalculate" needs the user's approval to modify the PO
    poModifyRequired: boolean;
    poModifyApproved: boolean;

    skipInvAutoRuleSetup?: boolean; // set front-end param
    reset?: boolean; // set front-end param: put the action back to the status default
}

export type LogiCtrlExistingAction = "skip" | "return" | "-" | null;

export type LogiCtrlChangeItem = {
    id: number;
    project: number;
    changeType: string; // 'new' | 'update'
    opening: number | null;
    openingName: string | null;
    openingType: "Single" | "Pair" | "Window" | "" | null;
    name: string;
    products: LogiCtrlChangeItemProduct[] | null;
    logictrl?: OpeningLogiCtrlStatus
}

export type LogiCtrlChangeItemProduct = {
    id: number;
    name: string;
    qty: number;
    changeType: string; // 'new' | 'update' | 'error' | 'pending' | 'leave'
    kind: string;
    subtype: OpeningType | null;
    error: string | null;
    needsExistingAction?: boolean;
    // the new item conflicts with an in-flight request; a Send or Skip rule for the address resolves it
    needsAddressRule?: boolean;
    conflictsWithMain?: boolean;
    existingLogisticsStatus?: string;
    // what happens to the existing item, shown in Result when there is no error
    note?: string;
    mode: "upsert" | "remove" | "duplicate" | "replace";
    // set when mode is "replace": a door/frame update condensed into one line
    replaces?: LogiCtrlChangeReplace | null;
}

export type LogiCtrlChangeReplace = {
    oldProduct: number;
    oldName: string;
    oldFields: LogiCtrlReviewField[] | null;
    oldPreps: Preps | null;
    oldInvRequests: number[] | null;
    oldStatus: string | null;

    newProduct: number;
    newName: string;
    newFields: LogiCtrlReviewField[] | null;
    newPreps: Preps | null;
}

export type LogiCtrlReviewField = {
    name: string;
    value: string;
}

export type LogiCtrlWorkOrderPrice = {
    id: number;
    project: number;
    changeset: number;
    name: string;
    qty: number;
    unitCostCents: Cents;
    markupPercent: number | null;
    markupCents: Cents | null;
    extendedPrice: Cents;
    updatedAt: string;
    projectWorkOrderItem: number | null;
}

export type LogiCtrlPendingChange = {
    id: number;
    opening: number | null;
    hardwareGroup: number | null;
    hardwareGroupItem: number | null;
    miscProduct: number | null;
    div10: number | null;
    kind: string;
    subtype: string | null;
    addProduct: number | null;
    errorStr: string | null;
    mode: string;
    unitCostCents: Cents;
    markupCents: Cents | null;
    markupPercent: number;
    extendedPrice: Cents;
    qty: number;
    openingName: string | null;

    productName: string | null;
    productCode: string | null;
    productCategoryName: string | null;

    status: string | null;
    distinctStatuses: number;
}

